import { computed, getCurrentInstance, provide } from 'vue'

import { localeContextKey } from '@ll-plus/hooks'
import { theme } from 'ant-design-vue'

import type { MaybeRef } from '@vueuse/core'
import type { App } from 'vue'

import { THEME_ENUM } from './config-provider'

type ConfigProviderContext = Partial<any>

const { darkAlgorithm, defaultAlgorithm } = theme

export const useConfigProvider = () => {
  const provideGlobalConfig = (
    config: MaybeRef<ConfigProviderContext>,
    app?: App
  ) => {
    const inSetup = !!getCurrentInstance()

    const provideFn = app?.provide ?? ((inSetup ? provide : undefined) as any)
    if (!provideFn) {
      console.warn(
        'provideGlobalConfig',
        'provideGlobalConfig() can only be used inside setup().'
      )
      return
    }
    provideFn(
      localeContextKey,
      computed(() => config.value.locale)
    )
  }
  const getRootVarible = (root: any, name: string) => {
    return root.getPropertyValue(name).trim()
  }
  // 获取主题
  const getTheme = (root: any, name?: THEME_ENUM) => {
    const root_computed = getComputedStyle(root)
    // 1、品牌色
    const colorPrimaryBg = getRootVarible(root_computed, '--color-primary-bg')
    const colorPrimaryBgHover = getRootVarible(
      root_computed,
      '--color-primary-bg-hover'
    )
    const colorPrimaryBorder = getRootVarible(
      root_computed,
      '--color-primary-border'
    )
    const colorPrimaryBorderHover = getRootVarible(
      root_computed,
      '--color-primary-border-hover'
    )
    const colorPrimaryHover = getRootVarible(
      root_computed,
      '--color-primary-hover'
    )
    const colorPrimary = getRootVarible(root_computed, '--color-primary')
    const darkColorPrimary = getRootVarible(
      root_computed,
      '--dark-color-primary'
    )
    const colorPrimaryActive = getRootVarible(
      root_computed,
      '--color-primary-active'
    )
    const colorPrimaryTextHover = getRootVarible(
      root_computed,
      '--color-primary-text-hover'
    )
    const colorPrimaryText = getRootVarible(
      root_computed,
      '--color-primary-text'
    )
    const colorPrimaryTextActive = getRootVarible(
      root_computed,
      '--color-primary-text-active'
    )
    // 2.成功色
    const colorSuccess = getRootVarible(root_computed, '--color-success')

    // 3.警戒色
    const colorWarning = getRootVarible(root_computed, '--color-warning')

    // 4.错误色
    const colorError = getRootVarible(root_computed, '--color-error')

    // 5.信息色
    const colorInfo = getRootVarible(root_computed, '--color-info')

    //  6.中性色
    //  6.1文本色
    const colorTextBase = getRootVarible(root_computed, '--color-text-base')
    const colorText = getRootVarible(root_computed, '--color-text')
    const colorTextSecondary = getRootVarible(
      root_computed,
      '--color-text-secondary'
    )
    const colorTextTertiary = getRootVarible(
      root_computed,
      '--color-text-tertiary'
    )
    const colorTextQuaternary = getRootVarible(
      root_computed,
      '--color-text-quaternary'
    )
    // 6.2描边
    const colorBorder = getRootVarible(root_computed, '--color-border')
    const colorBorderSecondary = getRootVarible(
      root_computed,
      '--color-border-secondary'
    )
    const colorSplit = getRootVarible(root_computed, '--color-split')
    // 6.3填充
    const colorFill = getRootVarible(root_computed, '--color-fill')
    const colorFillSecondary = getRootVarible(
      root_computed,
      '--color-fill-secondary'
    )
    const colorFillTertiary = getRootVarible(
      root_computed,
      '--color-fill-tertiary'
    )
    const colorFillQuaternary = getRootVarible(
      root_computed,
      '--color-fill-quaternary'
    )
    // 6.4背景
    const colorBgBase = getRootVarible(root_computed, '--color-bg-base')

    const colorBgContainer = getRootVarible(
      root_computed,
      '--color-bg-container'
    )
    const colorBgElevated = getRootVarible(root_computed, '--color-bg-elevated')
    const colorBgLayout = getRootVarible(root_computed, '--color-bg-layout')
    const colorBgSpotlight = getRootVarible(
      root_computed,
      '--color-bg-spotlight'
    )
    const colorBgMask = getRootVarible(root_computed, '--color-bg-mask')
    // 6.5浮层容器
    const controlItemBgHover = getRootVarible(
      root_computed,
      '--control-item-bg-hover'
    )
    // 7.字体
    const fontFamily = getRootVarible(root_computed, '--font-family')
    return {
      fontFamily,
      colorPrimaryBg,
      colorPrimaryBgHover,
      colorPrimaryBorder,
      colorPrimaryBorderHover,
      colorPrimaryHover,
      colorPrimary:
        name === THEME_ENUM['dark'] ? darkColorPrimary : colorPrimary,
      colorPrimaryActive,
      colorPrimaryTextHover,
      colorPrimaryText,
      colorPrimaryTextActive,
      colorSuccess,
      colorWarning,
      colorError,
      colorInfo,
      colorTextBase,
      colorText,
      colorTextSecondary,
      colorTextTertiary,
      colorTextQuaternary,
      colorBorder,
      colorBorderSecondary,
      colorSplit,
      colorFill,
      colorFillSecondary,
      colorFillTertiary,
      colorFillQuaternary,
      colorBgBase,
      colorBgContainer,
      colorBgElevated,
      colorBgLayout,
      colorBgSpotlight,
      colorBgMask,
      controlItemBgHover,

      // 尺寸高度配置
      controlHeightSM: 28,
      controlHeight: 36,
      controlHeightLG: 40,

      // 圆角配置
      borderRadiusSM: 6,
      borderRadius: 6,
      borderRadiusLG: 8,
    }
  }
  // 设置主题
  const setTheme = (name: THEME_ENUM) => {
    // 1.设置color-scheme(系统自动设置作系统会对用户界面进行调整，包括表单控件、滚动条和 CSS 系统颜色的使用值。)
    const root = document.documentElement
    root.style.colorScheme = 'light'
    // 2.如果有设置root类名和存在本地
    if (name) {
      root.className = name
    }
    // 3.从root获取主题色变量
    const theme = getTheme(root, name)
    // 4.设置变量到ant主题中
    return {
      token: theme,
      algorithm:
        name === THEME_ENUM['dark'] ? [darkAlgorithm] : [defaultAlgorithm],
    }
  }
  return {
    provideGlobalConfig,
    getTheme,
    setTheme,
  }
}
